import { GameDevGUI } from "@westpierstudio/core-module-oryx/utils/devgui";
import { AnticipationView } from "components/AnticipationView";
import { Background } from "components/Background";
import { CrossbowBonus } from "components/CrossbowBonus";
import { GameLogo } from "components/GameLogo";
import { Jackpot } from "components/jackpot/Jackpot";
import { Reel } from "components/reels/Reel";
import { Reels } from "components/reels/Reels";
import { ReelStopEase, SpinConfig, SpinType } from "components/reels/ReelsModel";
import { RetriggerWinPanel } from "components/RetriggerWinPanel";
import { TotalWinPanel } from "components/TotalWinPanel";
import { Transition } from "components/Transition";
import { GameServices } from "game/GameServices";
import { PrinceOfThievesGameController } from "game/PrinceOfThievesGameController";
import { PrinceOfThievesGameModel } from "game/PrinceOfThievesGameModel";
import { GameEvent } from "servertypes/ServerEvents";
import { SoundPlayer } from "sound/SoundPlayer";
import { SoundSystem } from "sound/SoundSystem";
import { Signal } from "type-signals";
import { copyToClipboard, devLog } from "utils/Misc";

type Components = {
    reels: Reels,
    crossbowBonus: CrossbowBonus,
    totalWinPanel: TotalWinPanel,
    retriggerWinPanel: RetriggerWinPanel
    transition: Transition,
    soundplayer: SoundPlayer
}

class PrinceOfThievesDebugGUISignals {
    printFlow = new Signal();
    setFlowVerbose = new Signal<(value: boolean) => void>();
    setTheme = new Signal<(value: GameEvent) => void>();
}

interface PrinceOfThievesDebugGUISettings {
    enableSpinConfigEditor: boolean,
    enableTransitionTest: boolean,
    enableCrossbowTest: boolean,
    enablePanelTest: boolean,
    enableJackpotTest: boolean,
    enableAnticipationTest: boolean,
    enableThemeTest: boolean
    forcerGroups: string[]
}

export class PrinceOfThievesDebugGUI extends GameDevGUI {
    customSignals = new PrinceOfThievesDebugGUISignals();
    private settings: PrinceOfThievesDebugGUISettings;

    constructor(protected override readonly services: GameServices, private readonly model: PrinceOfThievesGameModel,) {
        super(services);
        this.settings = services.resources.getSetting(`dev`);
        this.setupFpsDebug();
    }

    setupPrinceOfThieves(components: Components) {
        const { crossbowBonus, transition, reels, retriggerWinPanel, totalWinPanel, soundplayer } = components;
        this.setupSpinConfigEditor();
        this.setupCrossbowDebug(crossbowBonus);
        this.setupTransitionDebug(transition);
        this.setupPanelDebug(retriggerWinPanel, totalWinPanel);
        this.setupJackpotDebug(reels.jackpot);
        this.setupSoundLogging(soundplayer);
        this.setupAnticipationDebug(reels);
        this.setupToggleTheme();
    }

    private setupFpsDebug() {
        const ticker = this.services.application.ticker;
        const section = this.gui.addSubSection(`FPS`);
        for (const fps of [1, 5, 10, 30, 60]) {
            this.gui.addButton(`Set FPS: ${fps}`, () => {
                ticker.minFPS = 0;
                ticker.maxFPS = fps;
                input.setValue(fps);
            }, section);
        }
        this.gui.addButton(`Set FPS: Max`, () => {
            ticker.minFPS = 0;
            ticker.maxFPS = 0;
            input.setValue(0);
        }, section);
        const input = this.gui.addNumberSlider(`FPS`, { value: ticker.maxFPS }, 0, 400, 0.1, () => {
            ticker.minFPS = 0;
            ticker.maxFPS = input.getValue();
        }, true, section);
    }

    private setupSoundLogging(soundplayer: SoundPlayer) {
        const box = this.gui.addCheckBox(`soundSystem`, { value: soundplayer.enableLogging }, () => {
            soundplayer.enableLogging = box.getValue();
        }, this.defaultTabs.loggingTab);
    }

    private setupSpinConfigEditor(): void {

        if (this.settings.enableSpinConfigEditor) {

            const spinSection = this.gui.addSubSection(`Spin Config`);
            const spinTypes: SpinType[] = Object.keys(this.services.resources.getSetting(`reelSpinSettings`)) as SpinType[];

            for (const type of spinTypes) {

                const spinTypeSection = spinSection.addFolder(type);

                const spinConfig = this.model.reels.spinConfigs.get(type) as SpinConfig;
                const spinSettings = this.model.reels.getSettingsForConfig(spinConfig);

                for (const [key, item] of Object.entries(spinSettings)) {

                    const obj = { value: item };

                    if (typeof item === `number`) {

                        this.gui.addNumber(key, obj, () => {

                            (spinConfig as any)[key] = obj.value;
                            this.model.reels.updateSpinConfig(type, spinConfig);
                        }, spinTypeSection);

                    } else if (typeof item === `boolean`) {

                        this.gui.addCheckBox(key, obj, () => {

                            (spinConfig as any)[key] = obj.value;
                            this.model.reels.updateSpinConfig(type, spinConfig);
                        }, spinTypeSection);
                    } else {

                        const list = Object.values(ReelStopEase);
                        const state = { value: list[list.indexOf(item)] };

                        this.gui.addDropdown(key, list, state, () => {

                            (spinConfig as any)[key] = state.value;
                            this.model.reels.updateSpinConfig(type, spinConfig);
                        }, spinTypeSection);
                    }
                }

                this.gui.addButton(`Copy`, () => {

                    const spinSettings = this.model.reels.getSettingsForConfig(spinConfig);
                    copyToClipboard(JSON.stringify(spinSettings, null, `\t`));
                }, spinTypeSection);
            }

            this.gui.addButton(`Reset Defaults`, () => {
                this.model.reels.restoreDefaultSpinConfigs();
                this.gui.restoreSectionDefaults(spinSection);
            }, spinSection);
        }
    }

    private setupCrossbowDebug(crossbow: CrossbowBonus) {
        if (this.settings.enableCrossbowTest) {
            const section = this.gui.addSubSection(`Crossbow`);
            const multipliersInput = this.gui.addInput(`Multipliers`, { value: `2,3,4` }, undefined, section);
            const projectilesInput = this.gui.addInput(`Targets Hit`, { value: `3,4,5` }, undefined, section);
            const totalMultiplierInput = this.gui.addNumber(`Total Multiplier`, { value: 20 }, undefined, section);
            this.gui.addButton(`Test`, () => {
                crossbow.visible = true;
                const multipliers = JSON.parse(`[${multipliersInput.getValue()}]`);
                const projectiles = JSON.parse(`[${projectilesInput.getValue()}]`);
                const totalMultiplier = totalMultiplierInput.getValue();
                crossbow.startRevealMultipliers({ multipliers, projectiles, totalMultiplier, targetValues: {} }, this.model.reels.layout);
            }, section);
            this.gui.addButton(`Skip`, () => {
                crossbow.skip();
            }, section);
        }
    }

    private setupTransitionDebug(transition: Transition) {
        if (this.settings.enableTransitionTest) {
            const section = this.gui.addSubSection(`Transition`);
            this.gui.addButton(`Free game Transition`, () => {
                const start = Date.now();
                transition.reset();
                transition.doLeafTransition(() => devLog(Date.now() - start));
            }, section);
            this.gui.addButton(`Skip free game Transition`, () => {
                transition.skipLeafTransition();
            }, section);
            this.gui.addButton(`Base game Transition`, () => {
                transition.doTransitionToBase();
            }, section);
        }
    }

    private setupAnticipationDebug(reel: Reels) {
        // @ts-ignore
        const { anticipationView, reels } = reel;
        if (this.settings.enableAnticipationTest) {
            const section = this.gui.addSubSection(`Anticipation`);
            this.gui.addButton(`Show`, () => {
                anticipationView.show(reels[2]);
            }, section);
            this.gui.addButton(`Hide`, () => {
                anticipationView.hide();
            }, section);
        }
    }

    private setupJackpotDebug(jackpot: Jackpot) {
        if (this.settings.enableJackpotTest) {
            const section = this.gui.addSubSection(`Jackpot`);
            const input = this.gui.addInput(`Positions`, { value: `0,1,2,3,1` }, undefined, section);
            this.gui.addButton(`Start`, () => {
                const rows: number[] = JSON.parse(`[${input.getValue()}]`);
                const pattern = [];
                for (let i = 0; i < rows.length; i++) {
                    pattern.push({ column: i, row: rows[i] });
                }
                jackpot.beginOnPattern(pattern);
            }, section);
            this.gui.addButton(`Stop`, () => {
                jackpot.stopJackpot();
            }, section);
        }
    }

    private setupPanelDebug(retriggerWinPanel: RetriggerWinPanel, totalWinPanel: TotalWinPanel) {
        if (this.settings.enablePanelTest) {
            const section = this.gui.addSubSection(`Panels`);
            const input = this.gui.addNumber(`Display value`, { value: 0 }, undefined, section);
            this.gui.addButton(`Show Retrigger Panel`, () => {
                retriggerWinPanel.show(input.getValue());
            }, section);
            this.gui.addButton(`Show Total Win Panel`, () => {
                totalWinPanel.show(input.getValue(), 1);
            }, section);
        }
    }

    private setupToggleTheme() {
        if (this.settings.enableThemeTest) {
            const section = this.gui.addSubSection(`Theme`);
            this.gui.addButton(`Show Base Theme`, () => {
                this.customSignals.setTheme.dispatch(GameEvent.INITIAL_SPIN);
            }, section);
            this.gui.addButton(`Show Free Game Theme`, () => {
                this.customSignals.setTheme.dispatch(GameEvent.FREE_SPIN);
            }, section);
        }
    }
}